import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE } from "../../../config";
import Footer from "../../homepage/footer";
import { openPdfInNewTab, preparePdfWindow } from "../../../utils/pdfOpener";

const AQA = () => {
  const [aqaPapers, setAQAPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("all"); // all, foundation, higher

  useEffect(() => {
    const fetchAQAPapers = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/exams/exam-papers`);
        const filteredPapers = response.data.data.filter(
          (paper) => paper.exam_board === "AQA"
        );
        setAQAPapers(filteredPapers);
      } catch (error) {
        console.error("Error fetching AQA papers:", error);
        setError("Failed to load AQA exam papers.");
      } finally {
        setLoading(false);
      }
    };

    fetchAQAPapers();
  }, []);

  const handleViewFile = async (filePath) => {
    preparePdfWindow();

    try {
      const response = await axios.get(`${API_BASE}/api/exams/signed-url`, {
        params: { file_path: filePath },
      });
      const signedUrl = response.data.signedUrl;
      openPdfInNewTab(signedUrl);
    } catch (error) {
      console.error("Error generating signed URL:", error);
      toast.error("Failed to fetch the file. Please try again later.");
    }
  };

  // Group papers by year
  const groupPapersByYear = (papers) => {
    const grouped = {};
    papers.forEach(paper => {
      if (!grouped[paper.year]) {
        grouped[paper.year] = [];
      }
      grouped[paper.year].push(paper);
    });
    // Sort years descending
    return Object.keys(grouped)
      .sort((a, b) => b - a)
      .reduce((acc, year) => {
        acc[year] = grouped[year].sort((a, b) => {
          // Sort by month then paper number
          const monthOrder = { 'June': 1, 'November': 2, 'May': 0 };
          if (monthOrder[a.month] !== monthOrder[b.month]) {
            return monthOrder[a.month] - monthOrder[b.month];
          }
          return a.paper_number - b.paper_number;
        });
        return acc;
      }, {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600 font-semibold">Loading AQA Papers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
          <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-center text-teal-600 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  const foundationPapers = aqaPapers.filter((paper) => paper.level === "Foundation");
  const higherPapers = aqaPapers.filter((paper) => paper.level === "Higher");

  const getFilteredPapers = () => {
    if (selectedLevel === "foundation") return foundationPapers;
    if (selectedLevel === "higher") return higherPapers;
    return aqaPapers;
  };

  const filteredPapers = getFilteredPapers();
  const groupedPapers = groupPapersByYear(filteredPapers);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>AQA Exam Papers | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/aqa" />
        <meta name="description" content="Access past AQA GCSE Maths exam papers with questions and solutions to practise and improve your exam performance." />
      </Helmet>

      <main className="container mx-auto py-10 px-4 sm:px-6 lg:px-12 max-w-7xl flex-grow">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">AQA Exam Papers</h1>
          <p className="text-lg text-gray-600">Access question papers and mark schemes for AQA GCSE Maths exams</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-teal-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium mb-1">Total Papers</p>
                <p className="text-3xl font-bold text-gray-800">{aqaPapers.length}</p>
              </div>
              <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium mb-1">Foundation</p>
                <p className="text-3xl font-bold text-gray-800">{foundationPapers.length}</p>
              </div>
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium mb-1">Higher</p>
                <p className="text-3xl font-bold text-gray-800">{higherPapers.length}</p>
              </div>
              <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-2 mb-8 flex gap-2">
          <button
            onClick={() => setSelectedLevel("all")}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              selectedLevel === "all"
                ? "bg-teal-600 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            All Papers
          </button>
          <button
            onClick={() => setSelectedLevel("foundation")}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              selectedLevel === "foundation"
                ? "bg-blue-600 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Foundation
          </button>
          <button
            onClick={() => setSelectedLevel("higher")}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              selectedLevel === "higher"
                ? "bg-purple-600 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Higher
          </button>
        </div>

        {/* Papers by Year */}
        {Object.keys(groupedPapers).length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-600 font-medium text-lg">No exam papers available</p>
            <p className="text-gray-500 text-sm mt-2">Check back later for updated papers</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedPapers).map(([year, papers]) => (
              <div key={year} className="bg-white rounded-2xl shadow-xl overflow-hidden border-t-4 border-teal-500">
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {year} Exam Papers
                  </h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 gap-4">
                    {papers.map((paper) => (
                      <div
                        key={paper.id}
                        className="border-2 border-gray-100 rounded-xl p-6 hover:border-teal-200 hover:shadow-md transition-all"
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          {/* Paper Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                paper.level === "Foundation" 
                                  ? "bg-blue-100 text-blue-700" 
                                  : "bg-purple-100 text-purple-700"
                              }`}>
                                {paper.level}
                              </span>
                              <span className="text-gray-600 font-medium">
                                {paper.month} {year}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800">
                              Paper {paper.paper_number}
                            </h3>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-col sm:flex-row gap-3">
                            <button
                              onClick={() => handleViewFile(paper.file_url)}
                              className="flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Paper
                            </button>
                            {paper.ms_url ? (
                              <button
                                onClick={() => handleViewFile(paper.ms_url)}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Mark Scheme
                              </button>
                            ) : (
                              <div className="flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-400 rounded-lg font-semibold">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                No Mark Scheme
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AQA;